import React, { createContext, useContext } from 'react';

/**
 * `useFieldBinding` reads scoped field-error state through this seam instead
 * of importing a store directly — `packages/ui` is Tier 1 and cannot depend
 * on any app's Redux/Zustand instance. Both methods are themselves hooks
 * (not plain functions) so a concrete implementation can subscribe
 * per-field via its own store's selector hook, preserving the "one field at
 * a time" subscription granularity the original Redux-backed version had.
 */
export interface FieldErrorSource {
    /** Hook: current message (or undefined) for one scope+field pair. */
    useFieldMessage(scope: string, field: string): string | undefined;
    /** Hook: stable callback that clears one scope+field pair. */
    useClearField(scope: string, field: string): () => void;
}

const noopClear = () => {};

const noopFieldErrorSource: FieldErrorSource = {
    useFieldMessage: () => undefined,
    useClearField: () => noopClear,
};

const FieldErrorSourceContext = createContext<FieldErrorSource>(noopFieldErrorSource);
FieldErrorSourceContext.displayName = 'FieldErrorSourceContext';

export interface FieldErrorSourceProviderProps {
    value: FieldErrorSource;
    children: React.ReactNode;
}

/**
 * Mounted once per app shell. `web-content` backs this with `fieldErrorsSlice`
 * today; a future app (or a Zustand-migrated `web-content`, per Phase 7) can
 * supply its own local-state-backed implementation instead.
 */
export const FieldErrorSourceProvider: React.FC<FieldErrorSourceProviderProps> = ({
    value,
    children,
}) => <FieldErrorSourceContext.Provider value={value}>{children}</FieldErrorSourceContext.Provider>;

export function useFieldErrorSource(): FieldErrorSource {
    return useContext(FieldErrorSourceContext);
}
